var _dec, _dec2, _dec3, _class, _TopNavBarSmallViewportLayout, _IconXLine, _IconHamburgerLine;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Fragment, Children, Component } from 'react';
import { omitProps, withDeterministicId, callRenderProp } from '@instructure/ui-react-utils';
import { px } from '@instructure/ui-utils';
import { warn } from '@instructure/console';
import { testable } from '@instructure/ui-testable';
import { getBoundingClientRect, requestAnimationFrame } from '@instructure/ui-dom-utils';
import { withStyle, Global } from '@instructure/emotion';
import { Tray } from '@instructure/ui-tray';
import { IconXLine, IconHamburgerLine, IconArrowOpenDownSolid, IconArrowOpenUpSolid } from '@instructure/ui-icons';
import { Avatar } from '@instructure/ui-avatar';
import { Dialog } from '@instructure/ui-dialog';
import { Drilldown } from '@instructure/ui-drilldown';
import { TopNavBarItem } from '../../TopNavBarItem';
import { mapItemsForDrilldown, renderMappedItemDrilldownSubpages, renderMappedItemsAsDrilldownOptions } from '../../utils/mapItemsForDrilldown';
import { TopNavBarContext } from '../../TopNavBarContext';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { propTypes, allowedProps } from './props';
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
---
private: true
---
**/
let TopNavBarSmallViewportLayout = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyle, generateComponentTheme), _dec3 = testable(), _dec(_class = _dec2(_class = _dec3(_class = (_TopNavBarSmallViewportLayout = class TopNavBarSmallViewportLayout extends Component {
  constructor(props) {
    var _props$styles;
    super(props);
    this.ref = null;
    this._trayContainerId = void 0;
    this._trayId = void 0;
    this._drilldownId = void 0;
    this._menuTriggerId = void 0;
    this._menuId = void 0;
    this._inPlaceDialogId = void 0;
    this._inPlaceDialogCloseButtonId = void 0;
    this._separatorId = void 0;
    this._drilldownRef = null;
    this._raf = [];
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.renderOptionContent = (children, itemProps) => {
      const styles = this.props.styles;
      const status = itemProps.status,
        renderAvatar = itemProps.renderAvatar;
      let content = children;
      let optionStyle = status === 'active' ? styles === null || styles === void 0 ? void 0 : styles.dropdownMenuOptionActive : styles === null || styles === void 0 ? void 0 : styles.dropdownMenuOption;
      if (renderAvatar) {
        const avatarName = renderAvatar.avatarName,
          avatarSrc = renderAvatar.avatarSrc,
          avatarAlt = renderAvatar.avatarAlt;
        const label = avatarAlt || (typeof children === 'string' ? children : void 0);
        optionStyle = styles === null || styles === void 0 ? void 0 : styles.dropdownMenuOptionWithAvatar;
        content = _jsxs(Fragment, {
          children: [_jsx(Avatar, {
            name: avatarName,
            src: avatarSrc,
            alt: label,
            size: "small",
            margin: "0 small 0 0",
            role: "presentation",
            "aria-hidden": "true"
          }), children]
        });
      }
      return _jsx("span", {
        css: optionStyle,
        children: content
      });
    };
    this._trayContainerId = props.deterministicId('TopNavBarSmallViewportLayout-trayContainer');
    this._trayId = props.deterministicId('TopNavBarSmallViewportLayout-tray');
    this._menuId = props.deterministicId('TopNavBarSmallViewportLayout-menu');
    this._menuTriggerId = props.deterministicId('TopNavBarSmallViewportLayout-menuTrigger');
    this._drilldownId = props.deterministicId('TopNavBarSmallViewportLayout-drilldown');
    this._inPlaceDialogId = props.deterministicId('TopNavBarSmallViewportLayout-inPlaceDialog');
    this._inPlaceDialogCloseButtonId = props.deterministicId('TopNavBarSmallViewportLayout-inPlaceDialogCloseButton');
    this._separatorId = props.deterministicId('TopNavBarSmallViewportLayout-separator');
    this.state = {
      isDropdownMenuOpen: false,
      isDropdownMenuVisible: false,
      menuBottomPosition: px(((_props$styles = props.styles) === null || _props$styles === void 0 ? void 0 : _props$styles.navbarHeight) || 0)
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props, this.makeStylesVariables);
    this.updateMenuBottomPosition();
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2, this.makeStylesVariables);
  }
  componentWillUnmount() {
    this._raf.forEach(request => request.cancel());
    if (this.state.isDropdownMenuOpen) {
      this.toggleDropdownMenu();
    }
  }
  get makeStylesVariables() {
    return {
      isDropdownMenuVisible: this.state.isDropdownMenuVisible,
      drilldownId: this._drilldownId,
      trayId: this._trayId,
      menuBottomPosition: this.state.menuBottomPosition,
      inverseColor: this.context.inverseColor
    };
  }
  hasBrandBlock(renderBrand) {
    return !!renderBrand && !!renderBrand.props.renderIcon;
  }
  hasMenuItemsBlock(renderMenuItems) {
    return !!renderMenuItems && Children.count(renderMenuItems.props.children) > 0;
  }
  hasActionItemsBlock(renderActionItems) {
    return !!renderActionItems && Children.count(renderActionItems.props.children) > 0;
  }
  hasUserBlock(renderUser) {
    return !!renderUser && Children.count(renderUser.props.children) > 0;
  }
  get hasSubmenu() {
    return (this.dropdownMenuContent || []).length > 0;
  }
  get hasBreadcrumbBlock() {
    const renderBreadcrumb = this.props.renderBreadcrumb;
    return !!renderBreadcrumb && Children.count(renderBreadcrumb.props.children) > 0;
  }
  get isInPlaceDialogOpen() {
    var _this$props$renderInP;
    return (_this$props$renderInP = this.props.renderInPlaceDialogConfig) === null || _this$props$renderInP === void 0 ? void 0 : _this$props$renderInP.open;
  }
  get mappedUserOptions() {
    const renderUser = this.props.renderUser;
    if (!this.hasUserBlock(renderUser)) {
      return [];
    }
    const userChildren = Children.toArray(renderUser.props.children);
    return mapItemsForDrilldown(userChildren, {
      renderOptionContent: this.renderOptionContent
    });
  }
  get mappedMenuItemsOptions() {
    const renderMenuItems = this.props.renderMenuItems;
    if (!this.hasMenuItemsBlock(renderMenuItems)) {
      return [];
    }
    const menuItemsChildren = Children.toArray(renderMenuItems.props.children);
    return mapItemsForDrilldown(menuItemsChildren, {
      renderOptionContent: this.renderOptionContent,
      currentPageId: renderMenuItems.props.currentPageId
    });
  }
  get extractDrilldownSubpages() {
    return renderMappedItemDrilldownSubpages([...this.mappedUserOptions, ...this.mappedMenuItemsOptions]);
  }
  updateMenuBottomPosition() {
    const boundingRect = getBoundingClientRect(this.ref);
    this.setState({
      menuBottomPosition: boundingRect.top + boundingRect.height
    });
  }
  toggleDropdownMenu() {
    const onDropdownMenuToggle = this.props.onDropdownMenuToggle;
    const isDropdownMenuOpen = this.state.isDropdownMenuOpen;
    if (!isDropdownMenuOpen) {
      this.updateMenuBottomPosition();
    }
    if (typeof onDropdownMenuToggle === 'function') {
      onDropdownMenuToggle(!isDropdownMenuOpen);
    }
    this.setState({
      isDropdownMenuOpen: !this.state.isDropdownMenuOpen
    }, () => {
      if (this.state.isDropdownMenuOpen) {
        this.focusFirstAvailableItem();
      }
    });
  }
  focusFirstAvailableItem() {
    var _this$props$renderUse;
    const userChildren = Children.toArray((_this$props$renderUse = this.props.renderUser) === null || _this$props$renderUse === void 0 ? void 0 : _this$props$renderUse.props.children);

    // If option is a User, it preceeds other item, so focus that first
    const targetId = userChildren[0] ? userChildren[0].props.id : this.mappedMenuItemsOptions[0].optionData.id;
    setTimeout(() => {
      const container = document.getElementById(this._trayContainerId);
      const firstOption = container === null || container === void 0 ? void 0 : container.querySelector(`[id="${CSS.escape(targetId)}"]`);
      firstOption === null || firstOption === void 0 ? void 0 : firstOption.focus();
      if (this._drilldownRef) {
        const drilldownRef = this._drilldownRef;
        setTimeout(() => {
          if (drilldownRef) {
            // highlight the option using Drilldown's handleOptionHighlight function
            drilldownRef.handleOptionHighlight({}, {
              id: targetId
            });
          }
        }, 10);
      }
    }, 10);
  }
  renderMenuTrigger() {
    const _this$props3 = this.props,
      dropdownMenuToggleButtonLabel = _this$props3.dropdownMenuToggleButtonLabel,
      dropdownMenuToggleButtonTooltip = _this$props3.dropdownMenuToggleButtonTooltip,
      renderBrand = _this$props3.renderBrand,
      alternativeTitle = _this$props3.alternativeTitle,
      styles = _this$props3.styles;
    const isDropdownMenuOpen = this.state.isDropdownMenuOpen;
    let menuTrigger;
    const menuTriggerStyle = [styles === null || styles === void 0 ? void 0 : styles.menuTrigger, ...(alternativeTitle ? [styles === null || styles === void 0 ? void 0 : styles.alternativeTitleContainer] : [])];
    if (!this.hasSubmenu) {
      menuTrigger = null;
      if (alternativeTitle) {
        warn(false, 'There are no menu items or user menu to display in the <TopNavBar> dropdown menu! The menu trigger and the alternative title will not display.');
      }
    } else {
      const itemProps = {
        id: this._menuTriggerId,
        onClick: () => {
          this.toggleDropdownMenu();
        },
        tooltip: dropdownMenuToggleButtonTooltip,
        themeOverride: {
          itemSpacing: '0.375rem'
        },
        'aria-haspopup': 'menu',
        'aria-expanded': isDropdownMenuOpen,
        withFocusOutline: isDropdownMenuOpen ? true : void 0
      };
      const alternativeTitleIconProps = {
        size: 'x-small',
        style: {
          marginInlineEnd: '0.5em'
        }
      };
      menuTrigger = _jsx("div", {
        css: menuTriggerStyle,
        children: alternativeTitle ? _jsxs(TopNavBarItem, {
          ...itemProps,
          "aria-label": dropdownMenuToggleButtonLabel,
          children: [isDropdownMenuOpen ? _jsx(IconArrowOpenUpSolid, {
            ...alternativeTitleIconProps
          }) : _jsx(IconArrowOpenDownSolid, {
            ...alternativeTitleIconProps
          }), alternativeTitle]
        }) : _jsx(TopNavBarItem, {
          ...itemProps,
          variant: "icon",
          renderIcon: isDropdownMenuOpen ? _IconXLine || (_IconXLine = _jsx(IconXLine, {})) : _IconHamburgerLine || (_IconHamburgerLine = _jsx(IconHamburgerLine, {})),
          children: dropdownMenuToggleButtonLabel
        })
      });
    }
    return _jsxs("div", {
      css: styles === null || styles === void 0 ? void 0 : styles.menuTriggerContainer,
      children: [menuTrigger, !this.hasBreadcrumbBlock && !this.props.trayMountNode && _jsx("div", {
        css: styles === null || styles === void 0 ? void 0 : styles.trayContainer,
        id: this._trayContainerId
      }), this.hasBrandBlock(renderBrand) && !alternativeTitle && _jsx("div", {
        css: styles === null || styles === void 0 ? void 0 : styles.brandContainer,
        children: renderBrand
      })]
    });
  }
  get dropdownMenuContent() {
    const _this$props4 = this.props,
      renderUser = _this$props4.renderUser,
      renderMenuItems = _this$props4.renderMenuItems;
    const hasMenuItems = this.hasMenuItemsBlock(renderMenuItems);
    const hasUser = this.hasUserBlock(renderUser);
    let pageChildren = [];
    if (hasUser) {
      pageChildren = renderMappedItemsAsDrilldownOptions(this.mappedUserOptions);
      if (hasMenuItems) {
        pageChildren.push(_jsx(Drilldown.Separator, {
          id: this._separatorId
        }, this._separatorId));
      }
    }
    if (hasMenuItems) {
      pageChildren = [...pageChildren, ...renderMappedItemsAsDrilldownOptions(this.mappedMenuItemsOptions)];
    }
    return pageChildren.length ? pageChildren : void 0;
  }
  renderDropdownMenu() {
    const _this$props5 = this.props,
      dropdownMenuLabel = _this$props5.dropdownMenuLabel,
      onDropdownMenuSelect = _this$props5.onDropdownMenuSelect;
    return _jsx(Drilldown, {
      ref: el => {
        this._drilldownRef = el;
      },
      id: this._drilldownId,
      rootPageId: this._menuId,
      label: dropdownMenuLabel,
      height: "100%",
      width: "100%",
      onSelect: (e, args) => {
        if (typeof onDropdownMenuSelect === 'function') {
          onDropdownMenuSelect(e, args);
        }
        if (args.selectedOption.props.shouldCloseOnClick === 'auto' && !!args.selectedOption.props.href || args.selectedOption.props.shouldCloseOnClick === 'always') {
          this.toggleDropdownMenu();
        }
      },
      children: [_jsx(Drilldown.Page, {
        id: this._menuId,
        children: this.dropdownMenuContent
      }, this._menuId), ...this.extractDrilldownSubpages]
    });
  }
  renderDropdownMenuTray() {
    const trayMountNode = this.props.trayMountNode;
    if (!this.hasSubmenu) {
      return null;
    }
    return _jsx(Tray, {
      id: this._trayId,
      label: '',
      role: "none",
      open: this.state.isDropdownMenuOpen,
      transitionExit: false,
      onDismiss: () => {
        this.toggleDropdownMenu();
      },
      onKeyUp: e => {
        if (e.key === 'Escape') {
          this.toggleDropdownMenu();
        }
      },
      onEnter: () => {
        this.setState({
          isDropdownMenuVisible: true
        });
      },
      onExited: () => {
        this.setState({
          isDropdownMenuVisible: false
        });
      },
      shouldCloseOnDocumentClick: true,
      placement: "top",
      shadow: false,
      mountNode: trayMountNode || document.getElementById(this._trayContainerId),
      defaultFocusElement: () => document.getElementById(this._drilldownId),
      themeOverride: {
        position: 'absolute'
      },
      children: this.renderDropdownMenu()
    });
  }
  renderInPlaceDialog() {
    const _this$props6 = this.props,
      renderInPlaceDialogConfig = _this$props6.renderInPlaceDialogConfig,
      styles = _this$props6.styles;
    if (!renderInPlaceDialogConfig) {
      return null;
    }
    const content = renderInPlaceDialogConfig.content,
      open = renderInPlaceDialogConfig.open,
      onClose = renderInPlaceDialogConfig.onClose,
      closeButtonLabel = renderInPlaceDialogConfig.closeButtonLabel,
      shouldContainFocus = renderInPlaceDialogConfig.shouldContainFocus,
      shouldCloseOnEscape = renderInPlaceDialogConfig.shouldCloseOnEscape,
      shouldCloseOnDocumentClick = renderInPlaceDialogConfig.shouldCloseOnDocumentClick,
      returnFocusElement = renderInPlaceDialogConfig.returnFocusElement;
    const handleClose = () => {
      if (typeof onClose === 'function') {
        onClose();
      }
      if (returnFocusElement) {
        this._raf.push(requestAnimationFrame(() => {
          var _returnFocusElement;
          (_returnFocusElement = returnFocusElement()) === null || _returnFocusElement === void 0 ? void 0 : _returnFocusElement.focus();
        }));
      }
    };
    const dialog = _jsx(Dialog, {
      display: "block",
      open: open,
      shouldContainFocus: shouldContainFocus,
      shouldCloseOnEscape: shouldCloseOnEscape,
      shouldCloseOnDocumentClick: shouldCloseOnDocumentClick,
      onDismiss: handleClose,
      children: _jsxs("div", {
        id: this._inPlaceDialogId,
        css: styles === null || styles === void 0 ? void 0 : styles.inPlaceDialogContainer,
        children: [_jsx("div", {
          css: styles === null || styles === void 0 ? void 0 : styles.inPlaceDialogContainerContent,
          children: callRenderProp(content, {
            closeInPlaceDialog: handleClose
          })
        }), _jsx("div", {
          css: styles === null || styles === void 0 ? void 0 : styles.inPlaceDialogContainerButton,
          children: _jsx(TopNavBarItem, {
            id: this._inPlaceDialogCloseButtonId,
            renderIcon: IconXLine,
            variant: "icon",
            onClick: handleClose,
            children: closeButtonLabel
          })
        })]
      })
    });
    return dialog;
  }
  render() {
    const _this$props7 = this.props,
      navLabel = _this$props7.navLabel,
      renderActionItems = _this$props7.renderActionItems,
      renderBreadcrumb = _this$props7.renderBreadcrumb,
      styles = _this$props7.styles;
    return _jsxs("nav", {
      ...omitProps(this.props, allowedProps),
      ref: this.handleRef,
      "aria-label": navLabel,
      children: [_jsx(Global, {
        styles: styles === null || styles === void 0 ? void 0 : styles.globalStyles
      }), this.hasBreadcrumbBlock && _jsx("div", {
        css: styles === null || styles === void 0 ? void 0 : styles.navbar,
        children: renderBreadcrumb
      }), !this.hasBreadcrumbBlock && !this.isInPlaceDialogOpen && _jsxs("div", {
        css: styles === null || styles === void 0 ? void 0 : styles.navbar,
        children: [this.props.renderNavbarStartDangerousHack && this.props.renderNavbarStartDangerousHack, this.renderMenuTrigger(), this.hasActionItemsBlock(renderActionItems) && renderActionItems]
      }), !this.hasBreadcrumbBlock && this.renderInPlaceDialog(), !this.hasBreadcrumbBlock && this.renderDropdownMenuTray()]
    });
  }
}, _TopNavBarSmallViewportLayout.displayName = "TopNavBarSmallViewportLayout", _TopNavBarSmallViewportLayout.componentId = 'TopNavBar.SmallViewportLayout', _TopNavBarSmallViewportLayout.propTypes = propTypes, _TopNavBarSmallViewportLayout.allowedProps = allowedProps, _TopNavBarSmallViewportLayout.defaultProps = {}, _TopNavBarSmallViewportLayout.contextType = TopNavBarContext, _TopNavBarSmallViewportLayout)) || _class) || _class) || _class);
export { TopNavBarSmallViewportLayout };
export default TopNavBarSmallViewportLayout;